import React, { useState, useEffect } from 'react';
import { Search, Sparkles, Loader2, Download, Check, Image as ImageIcon, X, CheckSquare, Square, Maximize2 } from 'lucide-react';
import { webhookService, projectService } from '../../services/api';

interface ImageDownloadPanelProps {
  projectId: string;
  articleTitle?: string;
  brief?: any;
}

interface SelectableImage {
  id: string;
  url: string;
  selected: boolean;
  source: 'bank' | 'ai';
  name?: string;
}

export const ImageDownloadPanel: React.FC<ImageDownloadPanelProps> = ({
  projectId,
  articleTitle = '',
  brief
}) => {
  const [activeTab, setActiveTab] = useState<'bank' | 'ai'>('bank');
  const [searchTerm, setSearchTerm] = useState('');
  const [images, setImages] = useState<SelectableImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [previewImage, setPreviewImage] = useState<SelectableImage | null>(null);
  const [imageCount, setImageCount] = useState<number>(6); // Cantidad de imágenes a buscar

  const selectedCount = images.filter(img => img.selected).length;

  // Manejar tecla Escape para cerrar preview
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewImage) {
        setPreviewImage(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewImage]);

  // Abrir preview
  const openPreview = (img: SelectableImage, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewImage(img);
  };

  // Toggle selección desde preview
  const togglePreviewSelection = () => {
    if (previewImage) {
      toggleImageSelection(previewImage.id);
      setPreviewImage({ ...previewImage, selected: !previewImage.selected });
    }
  };

  // Helper para normalizar URL de imagen (soporta URLs, base64, objetos)
  const normalizeImageUrl = (img: any): string => {
    if (!img) return '';

    // Si es string
    if (typeof img === 'string') {
      // Ya es URL completa
      if (img.startsWith('http')) return img;
      // Ya es data URI
      if (img.startsWith('data:image')) return img;
      // Es base64 puro - detectar formato
      if (img.startsWith('/9j/')) return `data:image/jpeg;base64,${img}`;
      if (img.startsWith('iVBOR')) return `data:image/png;base64,${img}`;
      if (img.startsWith('R0lGOD')) return `data:image/gif;base64,${img}`;
      // Asumir PNG por defecto
      return `data:image/png;base64,${img}`;
    }

    // Si es objeto, extraer URL/data
    if (typeof img === 'object') {
      const extracted = img.url || img.src || img.data || img.output || img.image || img.base64 || '';
      return normalizeImageUrl(extracted);
    }

    return '';
  };

  // Buscar en banco de imagenes
  const searchBankImages = async () => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await webhookService.fetchExternalImages(searchTerm, imageCount);

      // Obtener array de imágenes (puede venir como images, urls, data, etc.)
      const rawImages = response.images || response.urls || response.data || [];

      const newImages: SelectableImage[] = rawImages
        .map((img: any, index: number) => {
          const url = normalizeImageUrl(img);
          if (!url) {
            return null;
          }
          return {
            id: `bank-${Date.now()}-${index}`,
            url,
            selected: false,
            source: 'bank' as const,
            name: `${searchTerm.replace(/\s+/g, '-')}-${index + 1}`
          };
        })
        .filter((img: SelectableImage | null): img is SelectableImage => img !== null);

      if (newImages.length === 0 && rawImages.length > 0) {
        setError('Las imágenes recibidas no tienen un formato válido');
      } else {
        setImages(prev => [...prev, ...newImages]);
      }
    } catch (err: any) {
      setError(err.message || 'Error buscando imagenes');
    } finally {
      setLoading(false);
    }
  };

  // Generar imagenes con IA
  const generateAiImages = async () => {
    const prompt = aiPrompt.trim() || articleTitle || 'imagen profesional';

    setLoading(true);
    setError('');

    try {
      const response = await webhookService.generateImages(
        prompt,                                           // tema
        undefined,                                        // referenceImage
        typeof brief === 'string' ? brief : undefined,    // researchSummary
        undefined,                                        // modificationPrompt
        'images',                                         // mode (only images, no infographics)
        projectId                                         // sessionId
      );

      const generatedImages = response.images || [];
      const newImages: SelectableImage[] = generatedImages.map((img: any, index: number) => {
        let url = img;
        if (typeof img === 'object') {
          url = img.url || img.src || img.output || img.data || '';
        }
        if (typeof url === 'string' && !url.startsWith('http') && !url.startsWith('data:')) {
          url = `data:image/png;base64,${url}`;
        }
        return {
          id: `ai-${Date.now()}-${index}`,
          url,
          selected: false,
          source: 'ai' as const,
          name: `ai-generated-${index + 1}`
        };
      });
      setImages(prev => [...prev, ...newImages]);
    } catch (err: any) {
      setError(err.message || 'Error generando imagenes');
    } finally {
      setLoading(false);
    }
  };

  // Toggle seleccion de imagen
  const toggleImageSelection = (id: string) => {
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, selected: !img.selected } : img
    ));
  };

  // Seleccionar/deseleccionar todas
  const toggleSelectAll = () => {
    const allSelected = images.every(img => img.selected);
    setImages(prev => prev.map(img => ({ ...img, selected: !allSelected })));
  };

  // Eliminar imagen de la lista
  const removeImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Optimizar y descargar imagenes seleccionadas
  const downloadSelectedImages = async () => {
    const selectedImages = images.filter(img => img.selected);
    if (selectedImages.length === 0) return;

    setDownloading(true);
    setError('');

    try {
      const response = await projectService.optimizeImagesForDownload(
        selectedImages.map(img => ({ url: img.url, name: img.name }))
      );

      if (response.success && response.images) {
        // Descargar cada imagen
        for (const img of response.images) {
          const link = document.createElement('a');
          link.href = `data:${img.mimeType};base64,${img.data}`;
          link.download = img.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Pequena pausa entre descargas
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Marcar como descargadas (deseleccionar)
        setImages(prev => prev.map(img => ({ ...img, selected: false })));
      }
    } catch (err: any) {
      setError(err.message || 'Error optimizando imagenes');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Download className="w-5 h-5 text-purple-600" />
            Imagenes para Descargar
          </h3>
          {images.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              {images.every(img => img.selected) ? (
                <>
                  <CheckSquare className="w-4 h-4" />
                  Deseleccionar todo
                </>
              ) : (
                <>
                  <Square className="w-4 h-4" />
                  Seleccionar todo
                </>
              )}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('bank')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'bank'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Search className="w-4 h-4" />
            Banco de Imagenes
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'ai'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Generar con IA
          </button>
        </div>
      </div>

      {/* Search/Generate Section */}
      <div className="bg-white border-b px-4 py-3">
        {activeTab === 'bank' ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchBankImages()}
              placeholder="Buscar imagenes (ej: marketing digital, oficina...)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {/* Selector de cantidad de imágenes */}
            <select
              value={imageCount}
              onChange={(e) => setImageCount(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              title="Cantidad de imágenes"
            >
              <option value={2}>2 img</option>
              <option value={4}>4 img</option>
              <option value={6}>6 img</option>
              <option value={8}>8 img</option>
              <option value={10}>10 img</option>
            </select>
            <button
              onClick={searchBankImages}
              disabled={loading || !searchTerm.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={`Prompt para generar (o dejar vacio para usar: "${articleTitle || 'tema del articulo'}")`}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={generateAiImages}
              disabled={loading}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generar
            </button>
          </div>
        )}

        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Images Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {images.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <ImageIcon className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">No hay imagenes</p>
            <p className="text-sm">
              {activeTab === 'bank'
                ? 'Busca imagenes en el banco de imagenes'
                : 'Genera imagenes con inteligencia artificial'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img) => (
              <div
                key={img.id}
                className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                  img.selected
                    ? 'border-purple-500 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => toggleImageSelection(img.id)}
              >
                <img
                  src={img.url}
                  alt={img.name || 'Imagen'}
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">Error</text></svg>';
                  }}
                />

                {/* Checkbox overlay */}
                <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                  img.selected
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/80 text-gray-400 group-hover:bg-white'
                }`}>
                  {img.selected ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-current" />
                  )}
                </div>

                {/* Expand button */}
                <button
                  onClick={(e) => openPreview(img, e)}
                  className="absolute top-2 right-10 w-6 h-6 rounded-full bg-blue-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-blue-600"
                  title="Ampliar imagen"
                >
                  <Maximize2 className="w-3 h-3" />
                </button>

                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(img.id);
                  }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                  title="Eliminar"
                >
                  <X className="w-3 h-3" />
                </button>

                {/* Source badge */}
                <div className={`absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${
                  img.source === 'ai'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {img.source === 'ai' ? 'IA' : 'Banco'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with Download Button */}
      {selectedCount > 0 && (
        <div className="bg-white border-t px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {selectedCount} imagen{selectedCount !== 1 ? 'es' : ''} seleccionada{selectedCount !== 1 ? 's' : ''}
          </span>
          <button
            onClick={downloadSelectedImages}
            disabled={downloading}
            className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Optimizando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Optimizar y Descargar ({selectedCount})
              </>
            )}
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
              <div className="flex items-center gap-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  previewImage.source === 'ai'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {previewImage.source === 'ai' ? 'Generada con IA' : 'Banco de imágenes'}
                </span>
                <span className="text-sm text-gray-600">{previewImage.name}</span>
              </div>
              <button
                onClick={() => setPreviewImage(null)}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Image */}
            <div className="flex items-center justify-center p-4 bg-gray-100 max-h-[60vh] overflow-auto">
              <img
                src={previewImage.url}
                alt={previewImage.name || 'Preview'}
                className="max-w-full max-h-[55vh] object-contain rounded-lg shadow-lg"
              />
            </div>

            {/* Footer with actions */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
              <button
                onClick={() => {
                  removeImage(previewImage.id);
                  setPreviewImage(null);
                }}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Eliminar
              </button>

              <button
                onClick={togglePreviewSelection}
                className={`px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  previewImage.selected
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {previewImage.selected ? (
                  <>
                    <Check className="w-4 h-4" />
                    Seleccionada
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4" />
                    Seleccionar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
